import type { CompoundEntityRef } from '@backstage/catalog-model';
import type { ConfigApi, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { TechDocsStorageClient } from '@backstage/plugin-techdocs';
import type { SyncResult, TechDocsStorageApi } from '@backstage/plugin-techdocs-react';
import { TechDocsRdapClient } from './techdocsRdapClient';
import type { TechDocsRdapVersionStore } from './versionStore';

export interface RdapTechDocsStorageApiOptions {
  configApi: ConfigApi;
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  versionStore: TechDocsRdapVersionStore;
}

/**
 * Wraps the stock TechDocsStorageClient so that, whenever the version picker
 * has selected a non-default branch/tag for an entity, doc content and sync
 * requests are redirected to the techdocs-rdap backend instead of the stock
 * one. Entities with no selection (or selection cleared back to the
 * registered branch) behave exactly as before.
 */
export class RdapTechDocsStorageApi implements TechDocsStorageApi {
  private readonly stock: TechDocsStorageClient;
  private readonly rdap: TechDocsRdapClient;
  private readonly versionStore: TechDocsRdapVersionStore;

  constructor(options: RdapTechDocsStorageApiOptions) {
    this.stock = new TechDocsStorageClient(options);
    this.rdap = new TechDocsRdapClient(options);
    this.versionStore = options.versionStore;
  }

  getApiOrigin(): Promise<string> {
    return this.stock.getApiOrigin();
  }

  getStorageUrl(): Promise<string> {
    return this.stock.getStorageUrl();
  }

  getBuilder(): Promise<string> {
    return this.stock.getBuilder();
  }

  getEntityDocs(entityId: CompoundEntityRef, path: string): Promise<string> {
    const ref = this.versionStore.get(entityId);
    if (!ref) {
      return this.stock.getEntityDocs(entityId, path);
    }
    return this.rdap.getEntityDocsForRef(entityId, ref, path);
  }

  syncEntityDocs(
    entityId: CompoundEntityRef,
    logHandler?: (line: string) => void,
  ): Promise<SyncResult> {
    const ref = this.versionStore.get(entityId);
    if (!ref) {
      return this.stock.syncEntityDocs(entityId, logHandler);
    }
    return this.rdap.syncEntityDocsForRef(entityId, ref, logHandler);
  }

  getBaseUrl(
    oldBaseUrl: string,
    entityId: CompoundEntityRef,
    path: string,
  ): Promise<string> {
    const ref = this.versionStore.get(entityId);
    if (!ref) {
      return this.stock.getBaseUrl(oldBaseUrl, entityId, path);
    }
    return this.rdap.getBaseUrlForRef(oldBaseUrl, entityId, ref, path);
  }
}
